import React, { useEffect, useState } from 'react';
import { X, Download, Calendar, Clock, Loader2, Check } from 'lucide-react';

export const ResultPop = ({ data, onClose }) => {
  const [downloading, setDownloading] = useState({
    input: false,
    output: false,
    result: false
  });
  const [downloadSuccess, setDownloadSuccess] = useState({
    input: false,
    output: false,
    result: false
  });
  const [brightness, setBrightness] = useState(100);
  
  // Close popup when Escape key is pressed
  useEffect(() => {
    if (!data) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, data]);
  
  if (!data) return null;

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const applyBrightnessToImage = (imageUrl, brightnessValue) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS for external images
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Apply brightness filter
        ctx.filter = `brightness(${brightnessValue}%)`;
        ctx.drawImage(img, 0, 0);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          resolve(url);
        }, 'image/png');
      };
      img.src = imageUrl;
    });
  };

  const handleDownload = async (imageUrl, type) => {
    try {
      setDownloading(prev => ({ ...prev, [type]: true }));
      setDownloadSuccess(prev => ({ ...prev, [type]: false }));
      
      // Create a more reliable filename
      const filename = `gomens-${data.type.toLowerCase()}-${type}-${Date.now()}`;
      
      let finalImageUrl = imageUrl;
      
      // Apply brightness if downloading output image and brightness is not default
      if (type === 'output' || type === 'result') {
        if (brightness !== 100) {
          finalImageUrl = await applyBrightnessToImage(imageUrl, brightness);
        }
      }
      
      // First try to fetch the image as a blob
      const response = await fetch(finalImageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${filename}.${blob.type.split('/')[1] || 'jpg'}`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        // Clean up brightness-adjusted URL if created
        if (finalImageUrl !== imageUrl) {
          window.URL.revokeObjectURL(finalImageUrl);
        }
      }, 100);
      
      setDownloadSuccess(prev => ({ ...prev, [type]: true }));
      
    } catch (error) {
      console.error(`Error downloading ${type} image:`, error);
      
      // Fallback to the original method if fetch fails
      const fallbackLink = document.createElement('a');
      fallbackLink.href = imageUrl;
      fallbackLink.download = `gomens-${data.type.toLowerCase()}-${type}-${Date.now()}.jpg`;
      fallbackLink.target = '_blank';
      fallbackLink.style.display = 'none';
      
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
      
      
    } finally {
      setDownloading(prev => ({ ...prev, [type]: false }));
    }
  };

  const formattedTime = formatDateTime(data.timestamp);

  return (
    <div 
      className="fixed inset-0 sm:inset-0 flex justify-center items-center bg-opacity-50 backdrop-blur-sm z-50 px-2 sm:px-0 py-4 sm:py-0"
    >
      <div 
        className="bg-white rounded-2xl p-2 sm:p-6 sm:mt-12 w-full max-w-2xl shadow-2xl relative mx-2 sm:mx-0 max-h-[calc(100vh-2rem)] sm:max-h-none overflow-y-auto sm:overflow-visible"
        style={{ maxWidth: 'min(90vw, 42rem)' }}
      >
        <button 
          onClick={onClose} 
          className="absolute top-2 right-2 sm:top-10 sm:right-4 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors duration-200 z-10"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>
        
        <div className="hidden md:flex items-center gap-2 mb-6">
          <div className="h-8 w-2 bg-gradient-to-b from-violet-600 to-indigo-600 rounded-full"></div>
          <h2 className="text-2xl font-bold text-gray-900">Result Details</h2>
        </div>

        <div className="flex flex-col gap-4 sm:gap-6 pb-4 sm:pb-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-100 pb-4 gap-2 sm:gap-0">
            <div className="inline-block px-3 py-1 text-sm font-semibold text-violet-600 bg-violet-100 rounded-full">
              {data.type}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-gray-600 w-full sm:w-auto">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4 text-violet-500 flex-shrink-0" />
                <span className="text-sm">{formattedTime.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-violet-500 flex-shrink-0" />
                <span className="text-sm">{formattedTime.time}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <p className="font-medium text-gray-800">Input Image</p>
                <button 
                  onClick={() => handleDownload(data.input_image, 'input')}
                  disabled={downloading.input}
                  className="text-violet-600 hover:text-violet-800 flex items-center justify-center sm:justify-start gap-1 text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
                >
                  {downloading.input ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : downloadSuccess.input ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="sm:hidden lg:inline">{downloading.input ? 'Downloading...' : 'Download'}</span>
                </button>
              </div>
              {/* Spacer to align with brightness slider on output side */}
              <div className="h-6"></div>
              <div className="relative overflow-hidden rounded-xl bg-gray-50 shadow-sm">
                <img 
                  src={data.input_image} 
                  alt="Input" 
                  className="w-full h-48 sm:h-56 lg:h-64 object-contain" 
                  loading="lazy"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <p className="font-medium text-gray-800">Output Image</p>
                <button 
                  onClick={() => handleDownload(data.output_image, 'output')}
                  disabled={downloading.output}
                  className="text-violet-600 hover:text-violet-800 flex items-center justify-center sm:justify-start gap-1 text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
                >
                  {downloading.output ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : downloadSuccess.output ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="sm:hidden lg:inline">{downloading.output ? 'Downloading...' : 'Download'}</span>
                </button>
              </div>
              {/* Brightness Control */}
              <div className="flex items-center gap-2 px-2">
                <span className="text-xs font-medium text-gray-600">Brightness:</span>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={brightness}
                  onChange={(e) => setBrightness(parseInt(e.target.value, 10))}
                  className="flex-1 h-1.5 accent-violet-600"
                />
                <span className="min-w-10 text-xs bg-violet-100 px-2 py-1 rounded text-violet-700 font-medium">
                  {brightness}%
                </span>
              </div>
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 shadow-sm">
                <img 
                  src={data.output_image} 
                  alt="Output" 
                  style={{ filter: `brightness(${brightness}%)` }}
                  className="w-full h-48 sm:h-56 lg:h-64 object-contain" 
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-2 sm:mt-4 pt-2 sm:pt-4 border-t border-gray-100">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200 order-1 w-full sm:w-auto"
            >
              Close
            </button>
            <button 
              onClick={() => handleDownload(data.output_image, 'result')}
              disabled={downloading.result}
              className="hidden sm:flex px-4 py-2 text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-lg font-medium shadow-sm transition-all duration-200 items-center justify-center gap-2 disabled:opacity-70 order-2 sm:order-2 w-full sm:w-auto"
            >
              {downloading.result ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : downloadSuccess.result ? (
                <Check className="w-4 h-4 text-green-300" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {downloading.result ? 'Saving...' : 'Save Result'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};